import type { Command } from "../contracts";
import { command } from "../contracts";
import type { NavigationStack } from "../navigation";
import { buildWorkspacePath, pushRecord } from "../navigation";

// The single canonical adapter that maps a raw server response from a button
// command into a follow-up Command for the bus. Replaces every ad-hoc
// "navigation hint" parser that used to live inside views.
//
// Accepts these dialects, in priority order:
//   1. ir.action.navigate { model_key, record_id, action_slug? }
//        → drill-down OPEN_RECORD with modelKey (preserves breadcrumb)
//   2. ir.action.url { url, target? }
//        → NAVIGATE (external URL — caller window.open's it)
//   3. ir.action.window { action_slug, view_mode?, record_id? }
//        → OPEN_ACTION
//   4. ir.action.client { action_slug?, params? }
//        → OPEN_ACTION when action_slug provided. The client-component
//          binding itself is resolved downstream by the action loader.
//   5. Flat hints { next_record_uuid, next_model_key?, next_action_slug? }
//        → OPEN_RECORD drill-down (modelKey) OR OPEN_ACTION fallback
//   6. Approval-gate { pending: true, approval_case_id }
//        → null (handler surfaces "Pending approval" UI in place)
//   7. Legacy { lead_id, action_slug } and friends
//        → mapped to the flat shape
//   8. Anything else → null (caller refetches in place)
//
// Returns a Command to dispatch on the same bus, or `null` when no navigation
// is implied.

export interface InterpretContext {
    currentSlug: string;
    currentStack: NavigationStack;
    orgCode: string;
}

interface AnyObject {
    [k: string]: unknown;
}

function isObject(v: unknown): v is AnyObject {
    return v !== null && typeof v === "object" && !Array.isArray(v);
}

function str(v: unknown): string | undefined {
    return typeof v === "string" && v.length > 0 ? v : undefined;
}

export function interpretServerAction(response: unknown, ctx: InterpretContext): Command | null {
    if (!isObject(response)) return null;

    // Approval-gate: handler created a case instead of running through. Surface
    // in place — no navigation.
    if (response.pending === true && (typeof response.approval_case_id === "string" || typeof response.case_id === "string")) {
        return null;
    }

    // Canonical: response is itself an `ir.action.*` envelope.
    const fromEnvelope = interpretEnvelope(response, ctx);
    if (fromEnvelope) return fromEnvelope;

    // Nested envelope under `.action` (the prevailing handler convention today).
    if (isObject(response.action)) {
        const nested = interpretEnvelope(response.action, ctx);
        if (nested) return nested;
        // Also try flat shape from inside the action object (some legacy
        // handlers nest the flat hints).
        const flat = interpretFlatHints(response.action, ctx);
        if (flat) return flat;
    }

    // Flat hints at the top level.
    return interpretFlatHints(response, ctx);
}

function interpretEnvelope(env: AnyObject, ctx: InterpretContext): Command | null {
    const actionType = str(env.action_type);
    if (!actionType) return null;
    switch (actionType) {
        case "ir.action.navigate": {
            // Drill-down — push the new record under the current breadcrumb.
            const modelKey = str(env.model_key);
            const recordId = str(env.record_id) ?? str(env.record_uuid);
            if (modelKey && recordId) {
                return command("OPEN_RECORD", { modelKey, recordRef: recordId });
            }
            // No modelKey → fall back to OPEN_ACTION if an action_slug exists.
            const slug = str(env.action_slug);
            if (slug && recordId) {
                return navigateToActionRecord(ctx, slug, recordId);
            }
            return null;
        }
        case "ir.action.url": {
            const url = str(env.url);
            if (!url) return null;
            return command("NAVIGATE", { to: url });
        }
        case "ir.action.window": {
            const slug = str(env.action_slug);
            if (!slug) return null;
            const recordId = str(env.record_id) ?? str(env.record_uuid);
            const viewMode = str(env.view_mode);
            if (recordId) return navigateToActionRecord(ctx, slug, recordId);
            return command("OPEN_ACTION", {
                actionKey: slug,
                viewMode: (viewMode === "list" || viewMode === "form" || viewMode === "kanban" || viewMode === "calendar")
                    ? viewMode
                    : undefined,
            });
        }
        case "ir.action.client": {
            // The action loader resolves `client_component` from the action
            // record. Caller just needs the slug to open. `params` may carry
            // a `record_id` to deep-link into a specific record of the
            // client action's underlying model.
            //
            // `target` (added by foundation.presentation Enh 08) — when
            // `"dialog"`, emit OPEN_ACTION_DIALOG so the action mounts as
            // a Radix Dialog frame instead of replacing the workspace
            // route. Used by the `actionEngine` (commandType="action") so
            // an `<button command-type="action">` opens the right shape.
            const slug = str(env.action_slug);
            if (!slug) return null;
            const params = isObject(env.params) ? env.params : undefined;
            const recordId = params
                ? (str(params.record_id) ?? str(params.record_uuid))
                : (str(env.record_id) ?? str(env.record_uuid));
            const target = str(env.target);
            if (target === "dialog") {
                return command("OPEN_ACTION_DIALOG", {
                    actionKey: slug,
                    recordRef: recordId ?? undefined,
                });
            }
            if (recordId) return navigateToActionRecord(ctx, slug, recordId);
            return command("OPEN_ACTION", { actionKey: slug });
        }
        default:
            return null;
    }
}

function interpretFlatHints(obj: AnyObject, ctx: InterpretContext): Command | null {
    const recordId = str(obj.next_record_uuid) ?? str(obj.record_uuid) ?? str(obj.record_id) ?? str(obj.lead_id);
    if (!recordId) return null;
    const modelKey = str(obj.next_model_key) ?? str(obj.model_key);
    if (modelKey) {
        return command("OPEN_RECORD", { modelKey, recordRef: recordId });
    }
    const slug = str(obj.next_action_slug) ?? str(obj.action_slug);
    if (slug) return navigateToActionRecord(ctx, slug, recordId);
    // No model key, no slug — best we can do is open the record in the current
    // action. Caller refetches.
    return command("OPEN_RECORD", { recordRef: recordId });
}

// Build a NAVIGATE command that lands on a fresh action's form view at the
// given record (used when the response targets a different action altogether
// — breadcrumb resets to the new action root + record).
function navigateToActionRecord(ctx: InterpretContext, actionSlug: string, recordId: string): Command {
    const path = buildWorkspacePath(ctx.orgCode, [
        { kind: "action", actionKey: actionSlug, viewMode: "form", recordRef: recordId },
    ]);
    return command("NAVIGATE", { to: path });
}

// Helper for tests that want to confirm drill-down is wired correctly through
// the navigation stack rather than dispatching a fresh action.
export function drillDownPath(stack: NavigationStack, orgCode: string, modelKey: string, recordRef: string): string {
    return buildWorkspacePath(orgCode, pushRecord(stack, {
        kind: "record",
        modelKey,
        recordRef,
        viewMode: "form",
    }));
}
